import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { buildFullLogoutOptions } from '../utils/logout';

/**
 * Result of useSessionExpired hook
 */
export interface UseSessionExpiredResult {
    /** Performs a full logout and sends the user back to the app origin */
    handleSessionExpired: () => void;
}

/**
 * Hook providing a full-logout handler for expired sessions.
 *
 * Terminates the central Auth0 SSO session (and the upstream IdP via
 * `federated`) so the user must re-authenticate from scratch, instead of
 * only dropping the local/organization session.
 *
 * Order matters: we only call `logout()` and do NOT mutate the Zustand auth
 * store first. The store is persisted to localStorage, so clearing
 * selectedOrgId here would re-render useAuthOrganizationValidation while
 * the logout redirect is in flight, causing it to fire a second logout()
 * (two competing /v2/logout navigations) and leaving the session
 * half-terminated. The stale org context is cleared on boot when the user
 * turns out not to be authenticated.
 *
 * @returns Object with the handleSessionExpired callback
 */
export const useSessionExpired = (): UseSessionExpiredResult => {
    const { logout } = useAuth0();

    const handleSessionExpired = useCallback(() => {
        logout(buildFullLogoutOptions());
    }, [logout]);

    return { handleSessionExpired };
};

export default useSessionExpired;
